<template>
  <main class="trace border rounded bg-body">
    <header class="bg-body-emphasis border-bottom p-3">
      <div
        class="d-flex flex-wrap align-items-center justify-content-between gap-3"
      >
        <div class="d-flex flex-wrap align-items-center gap-2">
          <h5 class="mb-0">Conversation #{{ conversation.id }}</h5>
          <span class="badge border text-body">{{
            conversationTypeLabel(conversation.conversation_type)
          }}</span>
          <span
            class="badge"
            :class="outcomePill(conversation.outcome).className"
            >{{ outcomePill(conversation.outcome).label }}</span
          >
          <span
            v-if="conversation.deleted"
            class="badge bg-secondary-subtle text-secondary-emphasis"
            >Archived</span
          >
        </div>
        <div class="d-flex flex-wrap gap-2">
          <a
            v-if="conversation.slack_permalink"
            class="btn btn-sm btn-outline-primary"
            :href="conversation.slack_permalink"
            target="_blank"
            rel="noopener noreferrer"
            >Slack thread</a
          >
          <a
            class="btn btn-sm btn-outline-primary"
            :href="buildGcpLogsUrl(conversation)"
            target="_blank"
            rel="noopener noreferrer"
            >GCP logs</a
          >
          <button
            class="btn btn-sm btn-outline-primary"
            @click="exportConversationJson(conversation)"
          >
            Export JSON
          </button>
        </div>
      </div>
      <dl class="meta-grid small mt-3 mb-0">
        <div>
          <dt>Company</dt>
          <dd>
            <RouterLink
              :to="{
                name: 'AdminPortfoliosView',
                query: { company_id: conversation.company.id },
              }"
              >{{ conversation.company.name }}</RouterLink
            ><span
              v-if="conversation.company.deleted"
              class="badge bg-secondary-subtle text-secondary-emphasis ms-1"
              >Archived</span
            >
            <div class="text-muted small">
              {{ conversation.company.country }} · ID
              {{ conversation.company.id }}
            </div>
          </dd>
        </div>
        <div>
          <dt>Account</dt>
          <dd>
            {{ conversation.account.email
            }}<span
              v-if="conversation.account.is_staff"
              class="badge bg-secondary-subtle text-secondary-emphasis ms-1"
              >Soldera</span
            >
            <div class="text-muted small">{{ conversation.account.name }}</div>
          </dd>
        </div>
        <div>
          <dt>Started</dt>
          <dd>
            {{ formatDateTime(conversation.created_at) }}
            <div v-if="conversation.last_message_at" class="small text-muted">
              Last message {{ formatDateTime(conversation.last_message_at) }} ·
              {{ conversation.duration_seconds }} s
            </div>
          </dd>
        </div>
        <div>
          <dt>Type</dt>
          <dd>
            <code>{{ conversation.conversation_type }}</code>
          </dd>
        </div>
        <div>
          <dt>Models</dt>
          <dd>{{ conversation.models.join(", ") || "No model recorded" }}</dd>
        </div>
      </dl>
    </header>
    <div v-if="!conversation.turns.length" class="p-4">
      <h5>Empty</h5>
      <p class="text-muted mb-0">0 turns, nothing logged</p>
    </div>
    <div v-else class="p-3">
      <p class="small text-muted">
        Turns are inferred from the stored message order. Consecutive user
        records may include more than one historical attempt.
      </p>
      <section v-for="(turn, index) in turns" :key="index" class="mb-4 p-0">
        <div class="small fw-semibold text-muted mb-3">
          Inferred turn {{ index + 1 }} ·
          <span class="badge" :class="outcomePill(turn.outcome).className">{{
            outcomePill(turn.outcome).label
          }}</span>
        </div>
        <div
          v-for="entry in turn.entries"
          :key="entry.message.id"
          class="message-row mb-3"
        >
          <span
            class="avatar"
            :class="
              entry.message.role === 'assistant' ? 'assistant-avatar' : ''
            "
            aria-hidden="true"
            >{{
              entry.message.role === "user"
                ? initialsFor(conversation.account)
                : entry.message.role === "system"
                  ? "S"
                  : "✧"
            }}</span
          >
          <AiMessageCardComponent
            :message="entry.message"
            :expandDocument="turn.rejected"
            :extractionPhase="entry.extractionPhase"
          />
        </div>
        <p
          v-if="turn.outcome === 'unknown'"
          class="rounded border border-warning-subtle bg-warning-subtle text-warning-emphasis p-3 small mb-0"
        >
          The stored trace does not establish whether this attempt completed. It
          may be unfinished or failed; inspect the recorded output and logs.
        </p>
      </section>
    </div>
  </main>
</template>
<script setup lang="ts">
import { computed } from "vue";
import { RouterLink } from "vue-router";
import { formatDateTime } from "@/composables/formatUtils";
import type { AiConversationDetail } from "./aiConversationsTypes";
import {
  buildGcpLogsUrl,
  conversationTypeLabel,
  exportConversationJson,
  initialsFor,
  outcomePill,
} from "./aiConversationsPresenters";
import AiMessageCardComponent from "./AiMessageCardComponent.vue";
const props = defineProps<{ conversation: AiConversationDetail }>();
const turns = computed(() =>
  props.conversation.turns.map((turn) => {
    const extractions = turn.messages.filter(
      (message) => message.step === "extraction"
    );
    return {
      outcome: turn.outcome,
      // Irrelevant uploads and chat corrections alike: show what the customer sent.
      rejected: turn.outcome === "rejected",
      entries: turn.messages.map((message) => ({
        message,
        extractionPhase:
          message.step === "extraction" && extractions.length > 1
            ? extractions.indexOf(message) + 1
            : undefined,
      })),
    };
  })
);
</script>
<style scoped lang="scss">
.trace {
  min-width: 0;
  overflow: hidden;
}
.meta-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.75rem;
}
dt {
  font-size: 0.65rem;
  text-transform: uppercase;
  color: var(--phoenix-secondary-color);
  margin-bottom: 0.25rem;
}
dd {
  margin: 0;
  overflow-wrap: anywhere;
}
code {
  color: inherit;
}
.message-row {
  display: grid;
  grid-template-columns: 30px minmax(0, 1fr);
  gap: 0.75rem;
  align-items: start;
}
.avatar {
  width: 30px;
  height: 30px;
  display: grid;
  place-items: center;
  border-radius: 50%;
  background: var(--phoenix-secondary-bg);
  font-size: 0.75rem;
  font-weight: 700;
}
.assistant-avatar {
  background: var(--phoenix-primary, #1f4d33);
  color: #fff;
  font-size: 1.2rem;
}
@media (max-width: 1000px) {
  .meta-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
@media (max-width: 500px) {
  .meta-grid {
    grid-template-columns: minmax(0, 1fr);
  }
  .message-row {
    grid-template-columns: minmax(0, 1fr);
  }
  .avatar {
    display: none;
  }
}
</style>
